// 결과 카드의 서술 한 문단. 완료한 기록과 데이터에서만 만들고, 구할 수 없는 문장은 뺀다(만들어 채우지 않는다).
// 문장 틀은 게임이 정한 것이고, 주제 문구는 서사 축 데이터(근거 카드는 draft), 전환 라벨은 승인된 전환 유형 표에서 온다.
import { GROUPS, GROUP_LABEL } from './rules.js';
import { summarize } from './summary.js';
import { buildResultCard } from './resultCard.js';
import { essenceOf } from './essence.js';
import { actOf } from './arc.js';
import { directionFor } from './arcDirection.js';
import { ARC_DATA } from './arcData.js';
import { LUCK_TRANSITIONS } from './luckTransitions.js';


const HANGUL_START = 0xac00, HANGUL_END = 0xd7a3;
/** 앞 낱말의 받침에 맞는 조사. pair 는 '은/는', '이/가', '을/를', '으로/로', '이에요/예요'. 한글이 아니면 받침이 없는 것으로 본다. */
export function josa(word, pair) {
  const [withFinal, without] = pair.split('/');
  const last = String(word ?? '').trim().slice(-1);
  if (!last) return without;
  const code = last.charCodeAt(0);
  if (code < HANGUL_START || code > HANGUL_END) return without;
  const final = (code - HANGUL_START) % 28;
  if (final === 0) return without;
  if (pair === '으로/로' && final === 8) return without;   // ㄹ 받침은 '로'
  return withFinal;
}

const signed = (n) => `${n > 0 ? '+' : n < 0 ? '-' : ''}${Math.abs(Math.round(n * 10) / 10)}`;

function openingSentence(run, endAge) {
  const stem = run.saju?.day?.stem;
  if (!Number.isInteger(stem)) return null;
  const essence = essenceOf(run.saju);
  const arc = essence ? ARC_DATA.arcs[essence.arcId.value] : null;
  if (arc) {
    // 결과 카드 제목과 같은 막별 문구(titlePhrase)로 처음과 끝의 시절을 말한다.
    const firstAct = arc.acts.find((a) => a.act === 1), lastAct = arc.acts.find((a) => a.act === (actOf(endAge) ?? 1));
    if (firstAct?.titlePhrase && lastAct?.titlePhrase) {
      if (firstAct.act === lastAct.act) return `이 사람은 ${endAge}세까지 ${firstAct.titlePhrase} 시절을 보냈어요.`;
      return `이 사람은 ${firstAct.titlePhrase} 시절에서 ${lastAct.titlePhrase} 시절로 이어진 ${endAge}세의 삶이에요.`;
    }
  }
  const direction = essence ? directionFor(essence, endAge) : null;
  const labels = (direction?.fields ?? []).map((f) => GROUP_LABEL[f]).filter(Boolean);
  if (labels.length) return `이 사람은 ${labels.join('·')} 쪽이 어울린다고 본 원국으로 ${endAge}세까지 살았어요.`;
  return `이 사람은 ${endAge}세까지 산 기록이에요.`;
}

function jobTitle(job) {
  if (!job) return null;
  return job.level <= 3 ? job.levels?.[job.level - 1] ?? job.name : job.branch?.name ?? job.levels?.[2] ?? job.name;
}

function jobSentence(run) {
  const moves = Array.isArray(run.jobHistory) ? run.jobHistory : [];
  const title = jobTitle(run.job);
  if (!run.job && !moves.length) return null;
  const finalName = run.job?.name ?? moves.at(-1)?.to?.name;
  const tail = title ? `, 마지막 직함은 ${title}${josa(title, '이에요/예요')}` : '';
  if (moves.length) {
    const first = moves[0].from?.name, last = moves.at(-1);
    if (first && last.to?.name) return `${first} 일을 하다 ${last.age}세에 ${last.to.name}${josa(last.to.name, '으로/로')} 바꿨고${tail}.`;
  }
  return finalName ? `일은 ${finalName}${josa(finalName, '으로/로')} 이어졌고${tail.replace(/^, /, ' ')}.` : null;
}

// 제목과 선택 문구는 아래 전환점 칸에 이미 있으므로 나이와 변화량만 말한다.
function highlightSentence(card) {
  const [gain, loss] = card.turningPoints;
  if (gain && loss) return `${gain.age}세에 가장 크게 올랐고(${gain.detail}), ${loss.age}세에 가장 크게 내려갔어요(${loss.detail}).`;
  if (gain) return `${gain.age}세에 가장 크게 올랐어요(${gain.detail}).`;
  if (loss) return `${loss.age}세에 가장 크게 내려갔어요(${loss.detail}).`;
  return null;
}

// 대운 구간(시작 나이별)에서 능력치 변화가 가장 컸던 구간
function luckSentence(run) {
  const groups = new Map();
  for (const year of Array.isArray(run.history) ? run.history : []) {
    if (!(year.age >= 15) || !year.luck || !Number.isFinite(year.luck.startAge) || !year.before || !year.stats) continue;
    const g = groups.get(year.luck.startAge) ?? { luck: year.luck, first: year, last: year };
    g.last = year;
    groups.set(year.luck.startAge, g);
  }
  let best = null;
  for (const g of groups.values()) {
    const nets = GROUPS.map((field) => [field, (g.last.stats[field] ?? 0) - (g.first.before[field] ?? 0)]).filter(([, d]) => Number.isFinite(d));
    const total = nets.reduce((t, [, d]) => t + Math.abs(d), 0);
    if (!total) continue;
    if (!best || total > best.total) best = { ...g, nets, total };
  }
  if (!best) return null;
  const [field, delta] = [...best.nets].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0];
  const label = best.luck.type ? LUCK_TRANSITIONS.labels?.[best.luck.type] : null;
  return `가장 크게 움직인 시기는 ${Math.ceil(best.luck.startAge)}세에 시작한 ${best.luck.ten} 운${label ? `(${label})` : ''}, 그동안 ${GROUP_LABEL[field]} ${signed(delta)}.`;
}

/** { sentences: [최대 4문장], text } — 기록에서 구할 수 있는 문장만 담는다. */
export function buildNarrative(run) {
  const endAge = summarize(run).endAge;
  const card = buildResultCard(run);
  const sentences = [openingSentence(run, endAge), jobSentence(run), highlightSentence(card), luckSentence(run)].filter(Boolean);
  return { sentences, text: sentences.join(' ') };
}
